import { Modal, Tooltip } from "antd";
import {
  ClockCircleOutlined,
  FireOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useMenuAllergens, useMenuTags } from "../../../../hooks/useMenu";
import type { Item, Allergen, Tag as ItemTag } from "../../../../api/menu";
import {
  getItemName,
  getItemDescription,
  getItemUnitAndSize,
  getTranslation,
} from "../../../../utils/translations";
import { useTranslation } from "react-i18next";
import { posModalStyles as modalStyles } from "../modalPrimitives";
import { useViewport } from "../../../../hooks/useViewport";
import { R } from "../orderingTheme";
import { ModalHeader } from "../modalPrimitives";

interface ItemDetailModalProps {
  item: Item | null;
  open: boolean;
  onClose: () => void;
}

export const ItemDetailModal = ({
  item,
  open,
  onClose,
}: ItemDetailModalProps) => {
  const { t, i18n } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const allAllergens = useMenuAllergens();
  const allTags = useMenuTags();

  const allergens: Allergen[] = item
    ? allAllergens.filter((a) => item.allergenIds.includes(a.id))
    : [];
  const tags: ItemTag[] = item
    ? allTags.filter((tt) => item.tagIds.includes(tt.id))
    : [];

  if (!item) return null;

  const itemName = getItemName(item.itemLangs, i18n.language, item.slug);
  const itemDescription = getItemDescription(item.itemLangs, i18n.language);
  const unitAndSize = getItemUnitAndSize(item.itemLangs, i18n.language);

  const width = isMobile ? undefined : isTablet ? 520 : 640;
  const bodyPad = isNarrow ? "16px" : "20px 24px";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={{ maxWidth: "calc(100vw - 32px)" }}
    >
      <div
        style={{ background: R.surface, fontFamily: R.font, color: R.text }}
      >
        <ModalHeader
          icon={<InfoCircleOutlined />}
          title={
            <>
              {itemName}
              {/* Единица/размер — muted до името, като в продуктовия ред */}
              {unitAndSize && (
                <span className="ml-2 font-pos-mono text-[15px] font-medium text-pos-muted whitespace-nowrap">
                  {unitAndSize}
                </span>
              )}
            </>
          }
          subtitle={item.price ? `${item.price.toFixed(2)} €` : undefined}
          onClose={onClose}
          isNarrow={isNarrow}
        />

        <div
          style={{
            padding: bodyPad,
            display: "flex",
            flexDirection: "column",
            gap: 14,
          }}
        >
          {item.images && (
            <div
              style={{
                width: "100%",
                height: isNarrow ? 180 : 220,
                background: R.bg,
                borderRadius: 12,
                overflow: "hidden",
                border: `1px solid ${R.border}`,
              }}
            >
              <img
                src={item.images}
                alt={itemName}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
                onError={(e) => {
                  e.currentTarget.src =
                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f3f4f6' width='100' height='100'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-size='40'%3E📷%3C/text%3E%3C/svg%3E";
                }}
              />
            </div>
          )}

          {itemDescription && (
            <p
              style={{
                margin: 0,
                fontSize: 13.5,
                fontWeight: 500,
                color: R.textSoft,
                lineHeight: 1.55,
              }}
            >
              {itemDescription}
            </p>
          )}

          {allergens.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                justifyContent: "center",
              }}
            >
              {allergens.map((allergen) => {
                const allergenName = getTranslation(
                  allergen.translations,
                  i18n.language,
                  allergen.slug,
                );
                const imageSrc = allergen.image
                  ? allergen.image.startsWith("data:")
                    ? allergen.image
                    : `data:image/png;base64,${allergen.image}`
                  : null;
                return (
                  <Tooltip key={allergen.id} title={allergenName}>
                    {imageSrc ? (
                      <img
                        src={imageSrc}
                        alt={allergenName}
                        style={{
                          width: 32,
                          height: 32,
                          objectFit: "contain",
                          cursor: "help",
                        }}
                      />
                    ) : (
                      <Chip tone="danger">⚠️</Chip>
                    )}
                  </Tooltip>
                );
              })}
            </div>
          )}

          {(item.kcal || item.prepareTime || tags.length > 0) && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10,
              }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {item.kcal && (
                  <Chip tone="brand" icon={<FireOutlined />}>
                    {item.kcal} kcal
                  </Chip>
                )}
                {item.prepareTime && (
                  <Chip tone="brand" icon={<ClockCircleOutlined />}>
                    {item.prepareTime} {t("common.min")}
                  </Chip>
                )}
              </div>

              {tags.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {tags.map((tag) => {
                    const tagName = getTranslation(
                      tag.translations,
                      i18n.language,
                      tag.slug,
                    );
                    const imageSrc = tag.image
                      ? tag.image.startsWith("data:")
                        ? tag.image
                        : `data:image/png;base64,${tag.image}`
                      : null;
                    return (
                      <Tooltip key={tag.id} title={tagName}>
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={tagName}
                            style={{
                              width: 30,
                              height: 30,
                              objectFit: "contain",
                              cursor: "help",
                            }}
                          />
                        ) : (
                          <Chip tone="neutral">{tagName}</Chip>
                        )}
                      </Tooltip>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

const Chip = ({
  children,
  icon,
  tone = "neutral",
}: {
  children: React.ReactNode;
  icon?: React.ReactNode;
  tone?: "brand" | "neutral" | "danger";
}) => {
  const palette =
    tone === "brand"
      ? { bg: R.brandTint, color: R.brandDeep, border: "rgba(217,119,6,0.28)" }
      : tone === "danger"
        ? {
            bg: "var(--color-danger-tint)",
            color: "var(--color-danger-ink)",
            border: "rgba(185,28,28,0.25)",
          }
        : { bg: R.bg, color: R.textSoft, border: R.border };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "3px 10px",
        borderRadius: 14,
        background: palette.bg,
        color: palette.color,
        border: `1px solid ${palette.border}`,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "inherit",
      }}
    >
      {icon}
      {children}
    </span>
  );
};
